import React, { useMemo, useRef } from "react";
import { useCesiumContext } from "./CesiumContext";
import {
  ArcType,
  CallbackProperty,
  Camera,
  Cartesian3,
  Color,
  Entity,
  JulianDate,
  Matrix3,
  Matrix4,
  PolylineGlowMaterialProperty,
  Quaternion,
  Transforms,
  Math as CesiumMath,
} from "@cesium/engine";
import { useClockOnTick } from "../clock";
import { CartesianDisplay } from "../Position/CameraPosition";

import CoordinateDisplay from "../Position/CoordinateDisplay";
import CameraSpeedDisplay from "../Position/CameraSpeedDisplay"
import { findTerrainIntercept } from "./MapScene";
import { TraverseInfo } from "./TraverseInfo";

import { LabeledValue } from "../base/LabeledValue";
import { formatUnitValue } from "../../lib/convert";
import QMViewer from "src/lib/cesium/QuickMapViewer";
import Button, { ToggleButton } from "../base/Button";
import { LengthUnits } from "convert-units";
import { useAppSelector } from "../../hooks";
import { isDefined } from "src/lib/utils";
import { useSelectedEntity } from "./hooks";
import { ScreenSpaceEventHandler } from "@cesium/engine";
import { ScreenSpaceEventType } from "@cesium/engine";
import { ChevronDown, PlusIcon, XIcon } from "lucide-react";

import {
  DropdownMenuItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../base/DropDownMenu";
import { DataSource } from "@cesium/engine";


import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../base/Collapsible";
import { HeadingPitchRange } from "@cesium/engine";
import { EntityItem } from "../LayerList/Layer/LayerCzml";
import { SampledPositionProperty } from "@cesium/engine";

export default function SelectedEntityMenu() {
  const { map: viewer } = useCesiumContext();
  const isEnabled = useAppSelector(
    (state) => state.mapState.isCesiumEntityDetailsEnabled
  );
  const selectedEntity = useSelectedEntity(viewer);
  if (!isEnabled || !selectedEntity) return;
  return (
    <div className="absolute z-50 top-0 right-[335px] bg-qmdark-900/70 py-2 px-3">
      <EntityPositionInfo entity={selectedEntity} />
    </div>
  );
}

type EntityToEntityDescriptionProps = {
  from: Entity;
  to: Entity | Camera;
  viewer: QMViewer;
  name: string;
  onClose?: () => void;
};
function EntityToEntityDescription({
  from,
  to,
  viewer,
  name,
  onClose,
}: EntityToEntityDescriptionProps) {
  const [angle, setAngle] = React.useState<number>();
  const [distance, setDistance] = React.useState<number>();
  const [showEntity, setShowEntity] = React.useState<boolean>(false);
  
  const FPVEnabled = useAppSelector(
    (state) => state.firstPersonView.FPVEnabled
  );
  useClockOnTick(() => {
    if (!from || !to) {
      setDistance(undefined);
      setAngle(undefined);
      return;
    }
    if (!viewer || viewer.isDestroyed()) return;
    const time = viewer.clock.currentTime;
    const position1 = viewer.getEntityPosition(from, time);

    const position2 =
      to instanceof Camera
        ? (FPVEnabled ? undefined : to.positionWC)
        : viewer.getEntityPosition(to, time);
    // item orientation (quaternion)
    let orientation1 = from?.orientation?.getValue?.(time); // Quaternion
    
    if (!position1 || !position2) {
      setDistance(undefined);
      setAngle(undefined);
      return;
    }
    const distance = Cartesian3.distance(position1, position2);
    setDistance(distance);
    if (!orientation1) {
      // no rotation. Define the default one
      const transform = Transforms.eastNorthUpToFixedFrame(
        position1,
        viewer.scene.globe.ellipsoid
      );
      const TBF2L = new Matrix3();
      Matrix4.getRotation(transform, TBF2L);
      orientation1 = Quaternion.fromRotationMatrix(TBF2L);
    }
    //setIsVisible(!findTerrainIntercept(position1, position2, viewer.scene));
    if (!orientation1) {
      setAngle(undefined);
      return;
    }
    // find velicity orientation rotation matrix
    const velDirection = Matrix3.multiplyByVector(
      Matrix3.fromQuaternion(orientation1),
      Cartesian3.UNIT_X,
      new Cartesian3()
    );
    const entityToEntityVect = Cartesian3.subtract(
      position2,
      position1,
      new Cartesian3()
    );
    if (Cartesian3.magnitude(entityToEntityVect) > 0) {
      Cartesian3.normalize(entityToEntityVect, entityToEntityVect);
      // angle between velocity vector and entity to sun vector
      const entitySunAngleRad = Cartesian3.angleBetween(
        velDirection,
        entityToEntityVect
      );
      setAngle(CesiumMath.toDegrees(Math.abs(entitySunAngleRad)));
    } else {
      setAngle(0);
    }
  }, 100);
  React.useEffect(() => {
    if (!showEntity) return;
    const ent = viewer.entities.add({
      polyline: {
        positions: new CallbackProperty((time) => {
          if (from === undefined) return [];
          const fromPosition = viewer.getEntityPosition(from, time);
          const toPosition =
            to instanceof Camera
              ? to.position
              : viewer.getEntityPosition(to, time);
          if (!fromPosition || !toPosition) return [];
          return [fromPosition, toPosition];
        }, false),
        show: new CallbackProperty((time) => {
          if (viewer?.selectedEntity === undefined) return [];
          const fromPosition = viewer.getEntityPosition(from, time);
          const toPosition =
            to instanceof Camera
              ? to.position
              : viewer.getEntityPosition(to, time);
          if (!fromPosition || !toPosition) return false;
          return !findTerrainIntercept(fromPosition, toPosition, viewer.scene);
        }, false),
        arcType: ArcType.NONE,
        material: new PolylineGlowMaterialProperty({
          color: Color.PURPLE,
          glowPower: 0.5,
        }),
      },
    });

    return () => {
      viewer?.entities?.remove?.(ent);
    };
  }, [from, to, viewer, showEntity]);
  const distanceUnits = distance! > 1000 ? "km" : ("m" as LengthUnits);
  const distancePrecision = distanceUnits === "m" ? 1 : 3;
  return (
    <div className="border-t py-1 border-qmdark-100 mt-4 flex flex-col">
      <div className="flex flex-row items-center">
        <h3 className="label">To {name}</h3>
        <div className="ml-auto">
          {onClose && (
            <Button size="sq" variant="secondary" isQuiet onPress={onClose}>
              <XIcon size={16} />
            </Button>
          )}
        </div>
      </div>
      <LabeledValue
        label={`Distance`}
        value={formatUnitValue(distance, {
          from: "m",
          to: distanceUnits,
          precision: distancePrecision,
          showUnits: true,
        })}
      />
      <LabeledValue
        label={`Angle from LoS`}
        value={formatUnitValue(angle, {
          from: "deg",
          to: "deg",
          precision: 2,
          showUnits: true,
        })}
      />
      {/* hide for now, intercept isn't ready*/}
      {false && (
        <div>
          <ToggleButton
            size="sm"
            onPress={() => setShowEntity((val) => !val)}
            isSelected={showEntity}
          >
            Show Intercept
          </ToggleButton>
        </div>
      )}
    </div>
  );
}

const offsetCartesian = new Cartesian3(0, 0, 250);
function EntityPositionInfo({ entity }: { entity: Entity }) {
  const { map: viewer, toLonLat } = useCesiumContext();
  const [toEntities, setToEntities] = React.useState<Entity[]>([]);
  const [position, setPosition] = React.useState<Cartesian3 | undefined>(() =>
    viewer.getEntityPosition(entity, viewer?.clock?.currentTime)
  );
  const [entitySpeed, setEntitySpeed] = React.useState<number | undefined>(() => {
    if (!viewer || viewer.isDestroyed()) return;
    return 0;
  });
  const [bearing, setBearing] = React.useState<number | undefined>();
  const adjustedPosition = useMemo(() => {
    if (!position) return;
    return viewer.adjustCartesian(position);
  }, [position, viewer]);

  const prevCameraPositionAndTime = useRef<{ 
    position: Cartesian3,
    time: JulianDate } | undefined>(undefined);

  useClockOnTick(() => {
    let bearingAngle;
    if (entity) {
      const newPos = viewer.getEntityPosition(
        entity,
        viewer?.clock?.currentTime);
      setPosition(newPos);

      if (newPos && prevCameraPositionAndTime.current) {
        const elapsedTime = JulianDate.secondsDifference(viewer?.clock?.currentTime, prevCameraPositionAndTime.current.time); // sec
        setEntitySpeed(
          Cartesian3.distance(newPos, prevCameraPositionAndTime.current.position) / elapsedTime); // m/s
      } else {
        setEntitySpeed(undefined);
      }
      if (newPos) {
        // bearing (angle between velocity vector and north)
        const nextCameraPos = viewer.getEntityPosition(
          entity,
          JulianDate.addSeconds(viewer?.clock?.currentTime, 1, new JulianDate()));
        bearingAngle = viewer?.qtsLight?.getPointAzimuthAngle(nextCameraPos, newPos);

        if (!prevCameraPositionAndTime.current) {
          prevCameraPositionAndTime.current = {
            position: Cartesian3.clone(newPos),
            time: JulianDate.clone(viewer?.clock?.currentTime)
          }
        }
        Cartesian3.clone(newPos, prevCameraPositionAndTime.current.position);
        JulianDate.clone(viewer?.clock?.currentTime, prevCameraPositionAndTime.current.time);
      } else {
        prevCameraPositionAndTime.current = undefined;
      }
    } else {
      setPosition(undefined);
      setEntitySpeed(undefined);
    }
    setBearing(bearingAngle);
  }, 100);

  const cartesianArray = React.useMemo(() => {
    if (!adjustedPosition) return;
    return Cartesian3.pack(adjustedPosition, []) as [number, number, number];
  }, [adjustedPosition]);

  const cartoArray = React.useMemo(() => {
    if (!position) return;
    return toLonLat(position);
  }, [position, toLonLat]);

  return (
    <div>
      <div className="flex flex-row items-center w-full">
        <EntityItem entity={entity} map={viewer} onFlyToTarget={(entity: Entity) => {
          const dest = viewer.getEntityPosition(
            entity,
            viewer.clock.currentTime,
            offsetCartesian
          );
          if (dest) {
            viewer.cameraFlyToLookDownNorthUp(viewer.adjustCartesian(dest));
          } else {
            viewer.flyTo(entity, {
              offset: new HeadingPitchRange(0, -(Math.PI / 2), 250),
            });
          }
        }}/>
          <Button
            size="sq"
            variant="secondary"
            isQuiet
            onPress={() => (viewer.selectedEntity = undefined)}
          >
            <XIcon size={16} className="-mt-1"/>
          </Button>
      </div>
      {position && (
        <>
          <CartesianDisplay value={cartesianArray!} />
          <CoordinateDisplay value={cartoArray} />
          {entity.position instanceof SampledPositionProperty && 
            <div className="flex items-center space-x-4">
              <CameraSpeedDisplay value={entitySpeed} />
              <LabeledValue
                  minWidth="4em"
                  label={"Bearing"}
                  value={formatUnitValue(bearing, { from: "rad", to: "deg", precision: 2, showUnits: true })}
              />
            </div>}
          {entity !== viewer.qtsLight?.bodies?.SUN?.entity &&
            entity !== viewer.qtsLight?.bodies?.EARTH?.entity &&
            !isDefined(entity.path) && (
              <TraverseInfo entity={entity}></TraverseInfo>
            )}
        </>
      )}
      <Collapsible>
        <CollapsibleContent className="CollapsibleContent">
          {position && (
            <>
              <EntityToEntityDescription
                from={entity}
                to={viewer.camera}
                viewer={viewer}
                name="camera"
              />
              {isDefined(viewer?.qtsLight?.bodies?.SUN?.entity) &&
                entity !== viewer.qtsLight.bodies.SUN.entity && (
                  <EntityToEntityDescription
                    from={entity}
                    to={viewer?.qtsLight?.bodies?.SUN?.entity}
                    viewer={viewer}
                    name="Sun"
                  />
                )}
              {isDefined(viewer?.qtsLight?.bodies?.EARTH?.entity) &&
                entity !== viewer.qtsLight.bodies.EARTH.entity && (
                  <EntityToEntityDescription
                    from={entity}
                    to={viewer?.qtsLight?.bodies?.EARTH?.entity}
                    viewer={viewer}
                    name="Earth"
                  />
                )}
              {toEntities?.map((toEntity) => {
                return (
                  <EntityToEntityDescription
                    key={toEntity.id}
                    from={entity}
                    to={toEntity}
                    viewer={viewer}
                    name={toEntity.name ?? toEntity.id}
                    onClose={() =>
                      setToEntities((s) => s.filter((e) => e !== toEntity))
                    }
                  />
                );
              })}
            </>
          )}
          <div className="border-t py-1 border-qmdark-100 mt-4 flex flex-col">
            <EntityScreenSpaceSelector
              label={"Add Entity to Compare"}
              onSelect={(entity) => setToEntities((s) => s.concat([entity]))}
            />
          </div>
        </CollapsibleContent>
        <CollapsibleTrigger asChild>
          <div className="w-full flex items-center justify-center text-center text-muted hover:text-primary py-1 mt-1 -mb-2">
            <ChevronDown size={14} />
          </div>
        </CollapsibleTrigger>
      </Collapsible>
    </div>
  );
}


function preventDefault(e: Event) {
  //e.preventDefault();
}
function EntityScreenSpaceSelector({
  onSelect,
  label = "Select Entity",
}: {
  label?: string;
  onSelect: (entity: Entity) => void;
}) {
  const { map } = useCesiumContext();
  const [entities, setEntities] = React.useState<Entity[]>([]);
  const [isActive, setIsActive] = React.useState(false);
  React.useEffect(() => {
    if (!isActive) return;
    const handler = map.screenSpaceEventHandler;
    const defaultAction = handler.getInputAction(
      ScreenSpaceEventType.LEFT_CLICK
    );
    handler.setInputAction(
      function (movement) {
        const pickedObjects = map.scene.drillPick(movement.position);
        setEntities(pickedObjects?.map?.((p) => p.id) ?? []);
        setIsActive(false);
      } as ScreenSpaceEventHandler.PositionedEventCallback,
      ScreenSpaceEventType.LEFT_CLICK
    );
    return () => {
      handler.setInputAction(defaultAction, ScreenSpaceEventType.LEFT_CLICK);
    };
  }, [isActive]);

  const isEntitiesSelected = isDefined(entities) && entities.length > 0;
  return (
    <DropdownMenu
      open={isEntitiesSelected || isActive}
      onOpenChange={() => setEntities([])}
      modal={false}
    >
      <DropdownMenuTrigger>
        <ToggleButton
          isSelected={isActive}
          onPress={() => setIsActive((s) => !s)}
        >
          <PlusIcon size={14} className="mr-1" />
          {label}
        </ToggleButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onPointerDownOutside={preventDefault}
        onInteractOutside={preventDefault}
        onFocusOutside={preventDefault}
        autoFocus={false}
      >
        <DropdownMenuLabel>
          {isEntitiesSelected ? (
            "Select one of the entities below..."
          ) : (
            <span className="italic">Select an entity on map</span>
          )}
        </DropdownMenuLabel>
        {isEntitiesSelected && <DropdownMenuSeparator />}
        {entities.map((entity) => {
          const owner = entity.entityCollection?.owner;
          let ownerName;
          if (owner instanceof DataSource) {
            ownerName = owner.name;
          }
          return (
            <DropdownMenuItem
              key={entity.id}
              onSelect={() => {
                setEntities([]);
                onSelect(entity);
              }}
            >
              {ownerName && (
                <span className="text-xs font-light">{ownerName}</span>
              )}
              {entity.name ?? entity.id}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function getEntityName(entity: Entity) {
  const owner = entity.entityCollection?.owner;
  let ownerName;
  if (owner instanceof DataSource) {
    ownerName = owner.name;
  }
  return `${ownerName ? `${ownerName}: ` : ""}${entity.name ?? entity.id}`;
}
